import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Home from './Home';
import Method from './Method';
import Platform from './Platform';
import PrivacyPolicy from './PrivacyPolicy';
import CookiePolicy from './CookiePolicy';
import PricingPage from './components/billing/PricingPage';
import Result from './components/billing/Result';
import RequestAccess from './components/billing/RequestAccess';
import ProfilePage from './components/ProfilePage';
import Login from './components/Login';
import Signup from './components/Signup';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import CookieBanner from './components/CookieBanner';
import { initAnalyticsIfConsented } from './lib/analytics';
import { subscribeConsent } from './lib/consent';
import UserDashboard from './private/dashboard';
import PrivateRoute from './contexts/privateroute';
import AdminRoute from './contexts/adminroute';
import VerifyEmail from './components/verifyemail';
import AdminLayout from './admin/AdminLayout';
import Overview from './admin/pages/Overview';
import Users from './admin/pages/Users';
import Requests from './admin/pages/Requests';
import Announcements from './admin/pages/Announcements';
import AuditLog from './admin/pages/AuditLog';
import Settings from './admin/pages/Settings';
import Deck from './admin/pages/Deck';
import UseCases from './admin/pages/UseCases';

function App() {
  // Analytics must only ever init when consent is already on record
  // (initAnalyticsIfConsented checks this itself and no-ops otherwise), and
  // again live if the user grants it later via the banner/footer link.
  useEffect(() => {
    initAnalyticsIfConsented();
    return subscribeConsent((record) => {
      if (record.analytics) initAnalyticsIfConsented();
    });
  }, []);

  return (
    <div id="root">
      <div className="main-content">
        <AuthProvider>
          <LanguageProvider>
          <CookieBanner />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/metodo" element={<Method />} />
            <Route path="/piattaforma" element={<Platform />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/request-cards" element={<Navigate to="/pricing" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/dashboard" element={<PrivateRoute><UserDashboard /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

            {/* New admin section */}
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<Overview />} />
              <Route path="users" element={<Users />} />
              <Route path="requests" element={<Requests />} />
              <Route path="use-cases" element={<UseCases />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="settings" element={<Settings />} />
              <Route path="deck" element={<Deck />} />
            </Route>

            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing/success" element={<Result ok={true} />} />
            <Route path="/billing/cancel" element={<Result ok={false} />} />
            {/* Public: book owners may not have an account yet — RequestAccess
                itself collects sign-in/sign-up before writing the submission. */}
            <Route path="/redeem" element={<RequestAccess />} />
            <Route path="/request-access" element={<RequestAccess />} />

            {/* Legacy redirects */}
            <Route path="/admindashboard" element={<Navigate to="/admin" replace />} />
            <Route path="/adminmessages" element={<Navigate to="/admin/announcements" replace />} />
          </Routes>
          </LanguageProvider>
        </AuthProvider>
      </div>
    </div>
  );
}

export default App;
